<template>
  <div class="share-wrapper">
    <div class="share-card">

      <!-- En-tête branding -->
      <div class="share-header">
        <img src="@/assets/logo_accueil.png" alt="Pouzet-Park" class="brand-logo" />
        <p class="brand-subtitle">Partage de fichier sécurisé</p>
      </div>

      <!-- Chargement -->
      <div v-if="state === 'loading'" class="share-body center-content">
        <i class="fas fa-spinner fa-spin fa-2x loading-icon"></i>
        <p class="loading-text">Chargement du partage…</p>
      </div>

      <!-- Introuvable / expiré -->
      <div v-else-if="state === 'invalid'" class="share-body center-content">
        <i class="fas fa-unlink fa-3x error-icon"></i>
        <h2 class="error-title">Lien invalide ou expiré</h2>
        <p class="error-desc">{{ errorMessage }}</p>
      </div>

      <!-- Limite de téléchargements atteinte -->
      <div v-else-if="state === 'exhausted'" class="share-body center-content">
        <i class="fas fa-ban fa-3x error-icon"></i>
        <h2 class="error-title">Limite atteinte</h2>
        <p class="error-desc">Ce fichier a atteint son nombre maximum de téléchargements.</p>
      </div>

      <!-- Formulaire mot de passe -->
      <div v-else-if="state === 'password'" class="share-body">
        <div class="file-preview">
          <div class="file-icon-wrap" :class="fileTypeClass">
            <i :class="fileIcon"></i>
          </div>
          <div class="file-info">
            <p class="file-title">{{ share.title }}</p>
            <p v-if="share.description" class="file-desc">{{ share.description }}</p>
            <p class="file-meta">
              <span v-if="share.filename">{{ share.filename }}</span>
              <span v-if="share.size"> · {{ formatSize(share.size) }}</span>
            </p>
          </div>
        </div>

        <div class="owner-notice" v-if="share.share_owner">
          <i class="fas fa-user"></i>
          Partagé par <strong>{{ share.share_owner }}</strong>
        </div>

        <div class="expiry-notice" v-if="share.expires_at">
          <i class="fas fa-calendar-times"></i>
          Expire le <strong>{{ formatDate(share.expires_at) }}</strong>
        </div>

        <div class="password-section">
          <p class="password-label">
            <i class="fas fa-lock"></i>
            Ce fichier est protégé par un mot de passe.
          </p>
          <div class="password-row">
            <input
              v-model="passwordInput"
              :type="showPassword ? 'text' : 'password'"
              class="password-field"
              placeholder="Mot de passe"
              autocomplete="current-password"
              @keyup.enter="downloadFile"
            />
            <button class="btn-icon" @click="showPassword = !showPassword" type="button">
              <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <p v-if="passwordError" class="password-error">
            <i class="fas fa-exclamation-circle"></i> {{ passwordError }}
          </p>
          <button
            class="btn-download"
            :disabled="downloading || !passwordInput"
            @click="downloadFile"
          >
            <i class="fas" :class="downloading ? 'fa-spinner fa-spin' : 'fa-download'"></i>
            {{ downloading ? 'Téléchargement…' : 'Accéder et télécharger' }}
          </button>
        </div>
      </div>

      <!-- Prêt pour téléchargement -->
      <div v-else-if="state === 'ready'" class="share-body">
        <div class="file-preview">
          <div class="file-icon-wrap" :class="fileTypeClass">
            <i :class="fileIcon"></i>
          </div>
          <div class="file-info">
            <p class="file-title">{{ share.title }}</p>
            <p v-if="share.description" class="file-desc">{{ share.description }}</p>
            <p class="file-meta">
              <span v-if="share.filename">{{ share.filename }}</span>
              <span v-if="share.size"> · {{ formatSize(share.size) }}</span>
            </p>
          </div>
        </div>

        <div class="owner-notice" v-if="share.share_owner">
          <i class="fas fa-user"></i>
          Partagé par <strong>{{ share.share_owner }}</strong>
        </div>

        <div class="expiry-notice" v-if="share.expires_at">
          <i class="fas fa-calendar-times"></i>
          Expire le <strong>{{ formatDate(share.expires_at) }}</strong>
        </div>

        <div class="downloads-notice" v-if="share.max_downloads > 0">
          <i class="fas fa-download"></i>
          {{ share.download_count }} / {{ share.max_downloads }} téléchargement(s) utilisé(s)
        </div>

        <button
          class="btn-download"
          :disabled="downloading"
          @click="downloadFile"
        >
          <i class="fas" :class="downloading ? 'fa-spinner fa-spin' : 'fa-download'"></i>
          {{ downloading ? 'Téléchargement en cours…' : 'Télécharger le fichier' }}
        </button>
      </div>

      <!-- Succès -->
      <div v-else-if="state === 'done'" class="share-body center-content">
        <i class="fas fa-check-circle fa-3x success-icon"></i>
        <h2 class="success-title">Téléchargement lancé !</h2>
        <p class="success-desc">Le fichier <strong>{{ share.filename }}</strong> est en cours de téléchargement.</p>
        <button class="btn-secondary mt-2" @click="state = 'ready'">
          <i class="fas fa-redo"></i> Télécharger à nouveau
        </button>
      </div>

      <div class="share-footer">
        <span>Proposé par <span class="smallcap">Pouzet-Park</span></span>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify'

export default {
  name: 'SharePage',

  data() {
    return {
      state: 'loading',   // loading | invalid | exhausted | password | ready | done
      share: null,
      errorMessage: '',
      passwordInput: '',
      showPassword: false,
      passwordError: '',
      downloading: false
    }
  },

  computed: {
    token() {
      return this.$route.params.token
    },

    fileTypeClass() {
      const mime = this.share?.mime_type || ''
      if (mime.includes('pdf')) return 'ft-pdf'
      if (mime.includes('image')) return 'ft-image'
      if (mime.includes('excel') || mime.includes('spreadsheet')) return 'ft-excel'
      if (mime.includes('word') || mime.includes('document')) return 'ft-word'
      if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar')) return 'ft-archive'
      if (mime.includes('video')) return 'ft-video'
      return 'ft-default'
    },

    fileIcon() {
      const mime = this.share?.mime_type || ''
      if (mime.includes('pdf')) return 'fas fa-file-pdf'
      if (mime.includes('image')) return 'fas fa-file-image'
      if (mime.includes('excel') || mime.includes('spreadsheet')) return 'fas fa-file-excel'
      if (mime.includes('word') || mime.includes('document')) return 'fas fa-file-word'
      if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar')) return 'fas fa-file-archive'
      if (mime.includes('video')) return 'fas fa-file-video'
      return 'fas fa-file-alt'
    }
  },

  async mounted() {
    await this.loadShare()
  },

  methods: {
    async loadShare() {
      this.state = 'loading'
      try {
        const response = await fetch(`/api/v1.0/partages/${encodeURIComponent(this.token)}`)

        if (response.status === 404 || response.status === 410) {
          const data = await response.json().catch(() => ({}))
          this.errorMessage = data.error || 'Ce lien de partage n\'existe pas ou a expiré.'
          this.state = 'invalid'
          return
        }

        if (!response.ok) {
          this.errorMessage = 'Une erreur est survenue lors du chargement du partage.'
          this.state = 'invalid'
          return
        }

        const data = await response.json()
        // Normalisation des champs API
        if (data.expiration_date) {
          data.expires_at = new Date(data.expiration_date * 1000).toISOString()
        }
        this.share = data

        if (data.max_downloads > 0 && data.download_count >= data.max_downloads) {
          this.state = 'exhausted'
          return
        }

        this.state = data.requires_password ? 'password' : 'ready'
      } catch {
        this.errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.'
        this.state = 'invalid'
      }
    },

    async downloadFile() {
      if (this.downloading) return
      this.passwordError = ''
      this.downloading = true

      const headers = {}
      if (this.share?.requires_password && this.passwordInput) {
        headers['X-Share-Password'] = this.passwordInput
      }

      try {
        const response = await fetch(
          `/api/v1.0/partages/${encodeURIComponent(this.token)}/telecharger`,
          { method: 'GET', headers }
        )

        if (response.status === 401) {
          this.passwordError = 'Mot de passe incorrect.'
          return
        }

        if (response.status === 403) {
          this.state = 'exhausted'
          return
        }

        if (response.status === 404 || response.status === 410) {
          this.errorMessage = 'Ce lien a expiré ou n\'existe plus.'
          this.state = 'invalid'
          return
        }

        if (!response.ok) {
          toast.error('Erreur lors du téléchargement.')
          return
        }

        const json = await response.json()

        // base64 → Uint8Array
        const binary = atob(json.data)
        const compressed = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          compressed[i] = binary.charCodeAt(i)
        }

        // Décompression gzip native (DecompressionStream)
        const ds = new DecompressionStream('gzip')
        const writer = ds.writable.getWriter()
        writer.write(compressed)
        writer.close()
        const buffer = await new Response(ds.readable).arrayBuffer()

        // Nom de fichier depuis Content-Disposition, sinon fallback
        const disposition = response.headers.get('Content-Disposition') || ''
        const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"'\n]+)["']?/i)
        const filename = nameMatch
          ? decodeURIComponent(nameMatch[1])
          : (json.filename || this.share?.filename || 'fichier')

        const mime = this.share?.mime_type || 'application/octet-stream'
        const blob = new Blob([buffer], { type: mime })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        this.state = 'done'
      } catch {
        toast.error('Erreur réseau lors du téléchargement.')
      } finally {
        this.downloading = false
      }
    },

    formatSize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    formatDate(isoStr) {
      return new Date(isoStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.share-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: linear-gradient(160deg, #e8e8e8 0%, #c8c8c8 100%);
}

.share-card {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Header ── */
.share-header {
  background: #ffffff;
  border-bottom: 3px solid #C0272D;
  padding: 20px 24px 16px;
  text-align: center;
}

.brand-logo {
  max-width: 260px;
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto 8px;
}

.brand-subtitle {
  font-size: 12px;
  color: #888;
  margin: 0;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

/* ── Body ── */
.share-body {
  padding: 28px 24px;
  flex: 1;
}

.center-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

/* ── États ── */
.loading-icon { color: #1a6fc4; }
.loading-text  { color: #6c757d; font-size: 15px; margin: 0; }

.error-icon   { color: #dc3545; }
.error-title  { font-size: 18px; font-weight: 600; color: #2c3e50; margin: 0; }
.error-desc   { color: #6c757d; font-size: 14px; margin: 0; }

.success-icon  { color: #28a745; }
.success-title { font-size: 18px; font-weight: 600; color: #2c3e50; margin: 0; }
.success-desc  { color: #6c757d; font-size: 14px; margin: 0; }

/* ── Aperçu fichier ── */
.file-preview {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 16px;
}

.file-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.ft-pdf     { background: #fff0f0; color: #dc3545; }
.ft-image   { background: #f0f4ff; color: #4a6cf7; }
.ft-excel   { background: #f0fff4; color: #28a745; }
.ft-word    { background: #f0f4ff; color: #0d6efd; }
.ft-archive { background: #fff8f0; color: #fd7e14; }
.ft-video   { background: #f9f0ff; color: #6f42c1; }
.ft-default { background: #f8f9fa; color: #6c757d; }

.file-info { flex: 1; min-width: 0; }

.file-title {
  font-weight: 600;
  font-size: 15px;
  color: #2c3e50;
  margin: 0 0 4px;
  word-break: break-word;
}

.file-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0 0 4px;
  word-break: break-word;
}

.file-meta {
  font-size: 12px;
  color: #adb5bd;
  margin: 0;
}

/* ── Notices ── */
.expiry-notice,
.downloads-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6c757d;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 12px;
}

.expiry-notice i    { color: #D4952A; }
.downloads-notice i { color: #1a6fc4; }

.owner-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6c757d;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 12px;
}

.owner-notice i { color: #1a6fc4; }

/* ── Mot de passe ── */
.password-section { margin-top: 4px; }

.password-label {
  font-size: 14px;
  color: #495057;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.password-label i { color: #1a6fc4; }

.password-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.password-field {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.password-field:focus { border-color: #1a6fc4; }

.btn-icon {
  width: 40px;
  height: 40px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}

.btn-icon:hover { background: #f0f5fc; color: #1a6fc4; }

.password-error {
  color: #dc3545;
  font-size: 13px;
  margin: 0 0 10px;
}

/* ── Bouton téléchargement ── */
.btn-download {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #1a6fc4, #0d4f9e);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: opacity 0.2s, transform 0.1s;
  margin-top: 8px;
}

.btn-download:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.btn-download:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  padding: 10px 20px;
  background: #f8f9fa;
  color: #2c3e50;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.btn-secondary:hover { background: #e9ecef; }

.mt-2 { margin-top: 8px; }

/* ── Footer ── */
.share-footer {
  padding: 14px 24px;
  background: #f8f9fa;
  border-top: 3px solid #C0272D;
  text-align: center;
  font-size: 12px;
  color: #adb5bd;
}

.smallcap {
  font-variant: small-caps;
  font-weight: 600;
  color: black;
}

</style>
